import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ComboboxItem } from '@screentone/core';
import { Box, Button, FormLabel, Group, Input, MultiSelect } from '@screentone/core';

import { ConfirmationDialog } from 'components/confirmation-dialog/ConfirmationDialog';
import { useAlert } from 'contexts/alert/useAlert';
import { useStructureEditContext } from 'contexts/structure-edit/StructureEditContext';
import {
  ArtifactType,
  Layout,
  PublicationSettingSearchableContentType,
  useDeleteArtifactSettingMutation
} from 'data/generated/graphql';
import { useConvertedProperty } from 'hooks';
import { useIsAdmin } from 'hooks/roles';
import styles from './LayoutSettings.module.scss';

const ALL_CONTENT_TYPE_OPTIONS: ComboboxItem[] = Object.values(PublicationSettingSearchableContentType).map((v) => ({
  id: v,
  value: v,
  label: v
}));

interface LayoutSettingsProps {
  layout: Layout;
}

export const LayoutSettings = ({ layout }: LayoutSettingsProps) => {
  const publicationKey = useConvertedProperty() ?? '';
  const navigate = useNavigate();
  const { alertError } = useAlert();
  const isAdmin = useIsAdmin();
  const { updateLayoutName, updateContentTypes } = useStructureEditContext();
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [selectedContentTypes, setSelectedContentTypes] = useState<ComboboxItem[]>(() => {
    const stored = (layout.contentTypes ?? []).map((s) => s.toLowerCase());
    return ALL_CONTENT_TYPE_OPTIONS.filter((o) => stored.includes(String(o.value).toLowerCase()));
  });
  const { mutateAsync: deleteLayout, isLoading: isDeleting } = useDeleteArtifactSettingMutation();

  const handleDelete = async () => {
    try {
      await deleteLayout({ publicationKey, artifactId: layout.id, artifactType: ArtifactType.Layout });
      navigate(`/${publicationKey}/site-config/layouts`);
    } catch (err) {
      alertError(`Failed to delete layout: ${(err as Error).message}`);
      setConfirmDeleteOpen(false);
    }
  };

  return (
    <>
      <Box>
        <Box.Title>Layout Information</Box.Title>
        <Box.Content>
          <Group gap="md" direction="column">
            <Group.Item fullWidth>
              <Group gap="xs" direction="column" fullWidth>
                <FormLabel label="Layout ID" labelPosition="top" fullWidth />
                <Input type="text" value={layout.id} disabled />
              </Group>
            </Group.Item>
            <Group.Item fullWidth>
              <Group gap="xs" direction="column" fullWidth>
                <FormLabel label="Layout Name" labelPosition="top" fullWidth />
                <Input
                  type="text"
                  value={layout.name}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateLayoutName(e.target.value)}
                />
              </Group>
            </Group.Item>
            <Group.Item fullWidth>
              <Group gap="xs" direction="column" fullWidth>
                <FormLabel label="Content Types" labelPosition="top" fullWidth />
                <MultiSelect
                  items={ALL_CONTENT_TYPE_OPTIONS}
                  selectedItems={selectedContentTypes}
                  onSelectedItemsChange={(items: ComboboxItem[] | ((prev: ComboboxItem[]) => ComboboxItem[])) => {
                    const resolved = typeof items === 'function' ? items(selectedContentTypes) : items;
                    setSelectedContentTypes(resolved);
                    updateContentTypes(resolved.map((i) => String(i.value)));
                  }}
                  status="ready"
                />
              </Group>
            </Group.Item>
          </Group>
        </Box.Content>
      </Box>

      {isAdmin && (
        <Box>
          <Box.Title>Danger Zone</Box.Title>
          <Box.Content>
            <Button
              primary
              color="lava"
              className={styles.deleteButton}
              onClick={() => setConfirmDeleteOpen(true)}
              disabled={isDeleting}
            >
              Delete layout
            </Button>
          </Box.Content>
        </Box>
      )}

      {confirmDeleteOpen && (
        <ConfirmationDialog
          title="Delete layout"
          description={`Are you sure you want to delete "${layout.name}"? This cannot be undone.`}
          isLoading={isDeleting}
          confirmLabel="Delete"
          onClickFunc={() => void handleDelete()}
          showDialog={setConfirmDeleteOpen}
        />
      )}
    </>
  );
};
